import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { API } from "../../../api";
import { Job } from "../../../models";
import { UserContext } from "../../../hooks/UserContext";

export const useAgrianIssuesViewModel = () => {
	const currentUser = useContext(UserContext);
	const isDeveloper = currentUser?.isDeveloper ?? false;

	const [jobs, setJobs] = useState<Job[]>([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [fixingIds, setFixingIds] = useState<Set<string>>(new Set());
	const [fixingAll, setFixingAll] = useState(false);
	const [countdown, setCountdown] = useState<number | null>(null);
	const countdownRef = useRef<ReturnType<typeof setInterval>>(undefined);

	useEffect(() => {
		return () => {
			if (countdownRef.current) {
				clearInterval(countdownRef.current);
			}
		};
	}, []);

	const sleepWithCountdown = useCallback((seconds: number) => {
		return new Promise<void>((resolve) => {
			if (countdownRef.current) {
				clearInterval(countdownRef.current);
			}
			setCountdown(seconds);
			countdownRef.current = setInterval(() => {
				setCountdown((prev) => {
					if (prev !== null && prev <= 1) {
						clearInterval(countdownRef.current);
						resolve();
						return null;
					}
					return prev !== null ? prev - 1 : null;
				});
			}, 1000);
		});
	}, []);

	useEffect(() => {
		if (isDeveloper) {
			fetchJobs();
		}
	}, [isDeveloper]);

	const fetchJobs = async () => {
		setLoading(true);
		setError(null);
		try {
			const result = await API.Data.Jobs.issues.getAgrian();
			setJobs(result);
		} catch (err: any) {
			setError(`Failed to load Agrian issues: ${err.message || "Unknown error"}`);
		} finally {
			setLoading(false);
		}
	};

	const fixOne = async (id: string) => {
		setFixingIds((prev) => new Set(prev).add(id));
		setError(null);
		try {
			await API.Data.Jobs.issues.fixAgrian([id]);
			await sleepWithCountdown(15);
			await fetchJobs();
		} catch (err: any) {
			setError(`Failed to fix job: ${err.message || "Unknown error"}`);
		} finally {
			setFixingIds((prev) => {
				const next = new Set(prev);
				next.delete(id);
				return next;
			});
		}
	};

	const fixAll = async () => {
		const ids = jobs.map((j) => j.id);
		if (ids.length === 0) return;

		setFixingAll(true);
		setFixingIds(new Set(ids));
		setError(null);
		try {
			await API.Data.Jobs.issues.fixAgrian(ids);
			await sleepWithCountdown(120);
			await fetchJobs();
		} catch (err: any) {
			setError(`Failed to fix all jobs: ${err.message || "Unknown error"}`);
		} finally {
			setFixingAll(false);
			setFixingIds(new Set());
		}
	};

	return {
		isDeveloper,
		jobs,
		loading,
		error,
		fixingIds,
		fixingAll,
		fixOne,
		fixAll,
		countdown,
	};
};
